import { Container, Sprite, Graphics, Text, TextStyle, Point } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { WallSystem } from '@gameplay/building/WallSystem';
import { DoorSystem } from '@gameplay/building/DoorSystem';
import { RoomManager } from '@gameplay/management/rooms/RoomManager';
import { LayerSystem } from '@rendering/LayerSystem';
import { Resident, ResidentStats, ResidentPersonality } from './Resident';
import { PathfindingSystem } from './PathfindingSystem';

export interface ResidentManagerCallbacks {
  onResidentAdded: (resident: Resident) => void;
  onResidentRemoved: (residentId: string) => void;
  onResidentNeedsChanged: (resident: Resident) => void;
}

export class ResidentManager {
  private readonly gridSystem: GridSystem;
  private readonly wallSystem: WallSystem;
  private readonly doorSystem: DoorSystem;
  private readonly roomManager: RoomManager;
  private readonly layerSystem: LayerSystem;
  private readonly container: Container;
  private readonly pathfinding: PathfindingSystem;
  
  private residents: Map<string, Resident> = new Map();
  private residentSprites: Map<string, Container> = new Map();
  private callbacks?: ResidentManagerCallbacks;
  
  private readonly RESIDENT_NAMES = [
    'Eleanor', 'Margaret', 'Dorothy', 'Helen', 'Betty', 'Patricia',
    'Robert', 'James', 'John', 'William', 'Richard', 'Charles',
    'Alice', 'Marie', 'Florence', 'Mildred', 'Ruth', 'Frances',
    'George', 'Frank', 'Walter', 'Harold', 'Arthur', 'Albert'
  ];

  constructor(
    gridSystem: GridSystem, 
    wallSystem: WallSystem,
    doorSystem: DoorSystem,
    roomManager: RoomManager, 
    layerSystem: LayerSystem,
    container: Container,
    callbacks?: ResidentManagerCallbacks
  ) {
    this.gridSystem = gridSystem;
    this.wallSystem = wallSystem;
    this.doorSystem = doorSystem;
    this.roomManager = roomManager;
    this.layerSystem = layerSystem;
    this.container = container;
    this.callbacks = callbacks;
    this.pathfinding = new PathfindingSystem(gridSystem, wallSystem, doorSystem);
  }

  public update(deltaTime: number): void {
    this.residents.forEach(resident => {
      resident.update(deltaTime);
      this.updateResidentSprite(resident);
    });
  }

  public addResident(position: Point, customStats?: Partial<ResidentStats>): Resident {
    const stats: ResidentStats = {
      id: this.generateId(),
      name: this.getRandomName(),
      age: 65 + Math.floor(Math.random() * 25), // Age 65-90
      personality: this.getRandomPersonality(),
      moveSpeed: 0.5 + Math.random() * 0.3, // 0.5-0.8 tiles per second
      careLevel: 1 + Math.floor(Math.random() * 5), // 1-5
      currentRoom: null,
      assignedBedroom: null,
      ...customStats
    };

    const resident = new Resident(stats, position, this.gridSystem, this.roomManager);
    resident.setPathfindingSystem(this.pathfinding);
    this.residents.set(stats.id, resident);
    
    this.createResidentSprite(resident);
    this.callbacks?.onResidentAdded(resident);
    
    console.log(`🧓 Added resident: ${stats.name} (${stats.personality}, Care Level: ${stats.careLevel})`);
    return resident;
  }

  public removeResident(residentId: string): boolean {
    const resident = this.residents.get(residentId);
    if (!resident) return false;

    this.destroyResidentSprite(residentId);
    this.residents.delete(residentId);
    this.callbacks?.onResidentRemoved(residentId);
    
    return true;
  }

  public getResident(residentId: string): Resident | undefined {
    return this.residents.get(residentId);
  }

  public getAllResidents(): Resident[] {
    return Array.from(this.residents.values());
  }

  public getResidentsInRoom(roomId: string): Resident[] {
    return Array.from(this.residents.values()).filter(
      resident => resident.stats.currentRoom === roomId
    );
  }

  private createResidentSprite(resident: Resident): void {
    const residentContainer = new Container();
    
    // Create resident sprite (simple circle for now)
    const sprite = this.createResidentGraphic(resident);
    residentContainer.addChild(sprite);
    
    // Create name label
    const nameLabel = new Text(resident.stats.name, new TextStyle({
      fontFamily: 'Arial',
      fontSize: 10,
      fill: 0xffffff,
      stroke: '#000000',
      strokeThickness: 2
    }));
    nameLabel.anchor.set(0.5, 0);
    nameLabel.position.set(0, -25);
    residentContainer.addChild(nameLabel);
    
    // Create needs indicator
    const needsIndicator = this.createNeedsIndicator(resident);
    needsIndicator.position.set(0, -35);
    residentContainer.addChild(needsIndicator);
    
    // Position the container
    this.updateResidentSpritePosition(residentContainer, resident);
    
    // Add to scene
    this.layerSystem.add(residentContainer, 'Characters', new Point(resident.position.x, resident.position.y));
    this.residentSprites.set(resident.stats.id, residentContainer);
  }

  private createResidentGraphic(resident: Resident): Graphics {
    const graphic = new Graphics();
    
    // Color based on personality
    const color = this.getPersonalityColor(resident.stats.personality);
    const radius = 8 + (resident.stats.careLevel * 2); // Larger for higher care needs
    
    graphic.beginFill(color, 0.8);
    graphic.lineStyle(2, 0xffffff, 1);
    graphic.drawCircle(0, 0, radius);
    graphic.endFill();
    
    // Add activity indicator
    if (resident.activity !== 'idle') {
      graphic.beginFill(0xffff00, 0.6);
      graphic.drawCircle(radius - 2, -radius + 2, 4);
      graphic.endFill();
    }
    
    return graphic;
  }

  private createNeedsIndicator(resident: Resident): Graphics {
    const indicator = new Graphics();
    const barWidth = 30;
    const barHeight = 3;
    
    // Show most urgent need
    const urgentNeed = this.getMostUrgentNeed(resident);
    if (urgentNeed.level < 50) {
      const color = urgentNeed.level < 20 ? 0xff0000 : 0xffaa00;
      
      indicator.beginFill(0x000000, 0.5);
      indicator.drawRect(-barWidth/2, 0, barWidth, barHeight);
      indicator.endFill();
      
      indicator.beginFill(color, 0.8);
      indicator.drawRect(-barWidth/2, 0, (urgentNeed.level / 100) * barWidth, barHeight);
      indicator.endFill();
    }
    
    return indicator;
  }

  private getMostUrgentNeed(resident: Resident): {type: string, level: number} {
    const needs = [
      { type: 'hunger', level: resident.needs.hunger },
      { type: 'rest', level: resident.needs.rest },
      { type: 'social', level: resident.needs.social },
      { type: 'medical', level: resident.needs.medical },
      { type: 'bathroom', level: resident.needs.bathroom }
    ];
    
    return needs.reduce((lowest, current) => 
      current.level < lowest.level ? current : lowest
    );
  }

  private updateResidentSprite(resident: Resident): void {
    const spriteContainer = this.residentSprites.get(resident.stats.id);
    if (!spriteContainer) return;

    this.updateResidentSpritePosition(spriteContainer, resident);
    
    // Update sprite graphic
    const graphic = spriteContainer.children[0] as Graphics;
    graphic.clear();
    const newGraphic = this.createResidentGraphic(resident);
    graphic.beginFill(newGraphic.geometry.graphicsData[0].fillStyle.color as number, 0.8);
    graphic.lineStyle(2, 0xffffff, 1);
    graphic.drawCircle(0, 0, 8 + (resident.stats.careLevel * 2));
    graphic.endFill();
    
    // Update activity indicator
    if (resident.activity !== 'idle') {
      graphic.beginFill(0xffff00, 0.6);
      graphic.drawCircle(6 + (resident.stats.careLevel * 2), -(8 + resident.stats.careLevel * 2) + 2, 4);
      graphic.endFill();
    }
    
    // Update needs indicator
    const needsIndicator = spriteContainer.children[2] as Graphics;
    needsIndicator.clear();
    const newNeedsIndicator = this.createNeedsIndicator(resident);
    needsIndicator.beginFill(newNeedsIndicator.geometry.graphicsData[0]?.fillStyle.color as number || 0x000000);
    // Copy the needs indicator drawing logic here if needed
  }

  private updateResidentSpritePosition(container: Container, resident: Resident): void {
    const worldPos = resident.position;
    container.position.set(worldPos.x, worldPos.y);
    
    // Update layer system sorting
    const gridPos = this.gridSystem.worldToGrid(worldPos.x, worldPos.y);
    container.zIndex = 1000000 + (gridPos.y * 1000 + gridPos.x); // Characters render on top
  }

  private destroyResidentSprite(residentId: string): void {
    const container = this.residentSprites.get(residentId);
    if (container) {
      container.parent?.removeChild(container);
      container.destroy({ children: true });
      this.residentSprites.delete(residentId);
    }
  }

  private generateId(): string {
    return `resident_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
  }

  private getRandomName(): string {
    const availableNames = this.RESIDENT_NAMES.filter(name => 
      !Array.from(this.residents.values()).some(resident => resident.stats.name === name)
    );
    
    if (availableNames.length === 0) {
      return `Resident ${this.residents.size + 1}`;
    }
    
    return availableNames[Math.floor(Math.random() * availableNames.length)];
  }

  private getRandomPersonality(): ResidentPersonality {
    const personalities: ResidentPersonality[] = ['social', 'introverted', 'active', 'bookworm', 'complainer'];
    return personalities[Math.floor(Math.random() * personalities.length)];
  }

  private getPersonalityColor(personality: ResidentPersonality): number {
    const colors: Record<ResidentPersonality, number> = {
      'social': 0xff6b6b,      // Red - outgoing
      'introverted': 0x4ecdc4,  // Teal - calm
      'active': 0x45b7d1,      // Blue - energetic
      'bookworm': 0x96ceb4,    // Green - studious
      'complainer': 0xfeca57   // Yellow - attention-seeking
    };
    return colors[personality];
  }

  public spawnRandomResidents(count: number, rooms?: any[]): void {
    const floors = this.roomManager.getRooms().filter(room => room.category === 'bedroom');
    
    for (let i = 0; i < count; i++) {
      // Spawn in a random floor area or bedroom if available
      let spawnPosition: Point;
      
      if (floors.length > 0) {
        const randomRoom = floors[Math.floor(Math.random() * floors.length)];
        const tile = randomRoom.tiles[Math.floor(Math.random() * randomRoom.tiles.length)];
        spawnPosition = this.gridSystem.gridToWorld(tile.gridX, tile.gridY);
      } else {
        // Spawn in a random floor tile
        const allFloors = this.gridSystem.getAllObjects().filter(obj => obj.type === 'floor');
        if (allFloors.length > 0) {
          const randomFloor = allFloors[Math.floor(Math.random() * allFloors.length)];
          spawnPosition = this.gridSystem.gridToWorld(randomFloor.gridX, randomFloor.gridY);
        } else {
          // Default spawn position
          spawnPosition = new Point(100, 100);
        }
      }
      
      this.addResident(spawnPosition);
    }
  }
}